
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CertificateImportBatch } from '@/utils/certificateUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, Calendar } from 'lucide-react';

interface ImportBatchesListProps {
  onRefresh?: () => void;
}

export function ImportBatchesList({ onRefresh }: ImportBatchesListProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['certificateImportBatches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_import_batches')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as CertificateImportBatch[];
    },
  });
  
  const handleRefresh = () => {
    refetch();
    if (onRefresh) onRefresh();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error loading import batches: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={handleRefresh}>
          Refresh
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>Course Type</TableHead>
            <TableHead className="text-right">Total Imported</TableHead>
            <TableHead>Import Date</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead>File Name</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((batch) => (
              <TableRow key={batch.id}>
                <TableCell>{batch.state}</TableCell>
                <TableCell>{batch.course_type}</TableCell>
                <TableCell className="text-right font-medium">{batch.total_imported}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="flex items-center">
                    <Calendar className="mr-2 h-4 w-4" />
                    {new Date(batch.created_at).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>{batch.notes || '-'}</TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <FileText className="mr-2 h-4 w-4" />
                    {batch.file_name || 'Manual import'}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-4">
                No import batches found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
